import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import Divider from "@mui/material/Divider"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"

import { axiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import PasswordTextField from "../../../common/components/PasswordTextField"
import Row from "../../../common/components/Row"
import TextLink from "../../../common/components/TextLink"
import SegmentTracker from "../../../common/tracking"
import AuthTitle from "../components/AuthTitle"
import LoadingPaper from "../components/LoadingPaper"
import WatchHelpVideoLink from "../components/WatchHelpVideoLink"
import { sessionAccountReceived } from "../reducer"
import { authFieldSx } from "../userAuthStyles"
import { useRedirectLoggedInUser } from "../utils"

export default function LogIn() {
  const [isLoading, setIsLoading] = useState(false)
  const [loginEmail, setLoginEmail] = useState("")
  const [loginEmailError, setLoginEmailError] = useState(null)
  const [loginPassword, setLoginPassword] = useState("")
  const [
    alreadySignedUpUsingGoogleDialogOpen,
    setAlreadySignedUpUsingGoogleDialogOpen,
  ] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  useRedirectLoggedInUser()

  function handleLoginButtonClicked() {
    setIsLoading(true)
    axiosClient
      .post("/clients/api/login/", {
        email: loginEmail.toLowerCase().trim(),
        password: loginPassword,
      })
      .then(({ data }) => {
        dispatch(sessionAccountReceived(data))
        SegmentTracker.deImpersonate()
      })
      .catch(({ response: { data } }) => {
        if (data.can_set_password) {
          setLoginEmailError("Account needs to set password")
          setAlreadySignedUpUsingGoogleDialogOpen(true)
        } else {
          setLoginEmailError(data.email)
        }
      })
      .then(() => setIsLoading(false))
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!loginEmail || !loginPassword || isLoading) {
      return
    }
    handleLoginButtonClicked()
  }

  function handleLoginEmailChanged(e) {
    setLoginEmail(e.target.value)
    setLoginEmailError(null)
  }

  return (
    <>
      <LoadingPaper isLoading={isLoading}>
        <AuthTitle
          title="Welcome back"
          subtitle="Sign in to recruit and manage your real estate agents."
        />
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ width: "100%" }}
        >
          <Column gap={2.5}>
            <TextField
              label="Email Address"
              type="email"
              size="small"
              value={loginEmail}
              onChange={handleLoginEmailChanged}
              error={!!loginEmailError}
              helperText={loginEmailError}
              required
              autoComplete="email"
              fullWidth
              sx={authFieldSx}
            />
            <PasswordTextField
              label="Password"
              size="small"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
              autoComplete="current-password"
              fullWidth
              sx={authFieldSx}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={!loginEmail || !loginPassword || isLoading}
              fullWidth
              sx={{ mt: 0.5, borderRadius: 3 }}
            >
              Log In
            </Button>
            <Divider sx={{ my: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                or
              </Typography>
            </Divider>
            <Button
              type="button"
              variant="outlined"
              size="large"
              onClick={() => navigate("/user/sign-up")}
              fullWidth
              sx={{ borderRadius: 3 }}
            >
              Create an account
            </Button>
            <Row
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mt: 0.5,
              }}
            >
              <TextLink to="/user/forgot-password" text="Forgot password?" />
              <WatchHelpVideoLink />
            </Row>
          </Column>
        </Box>
      </LoadingPaper>
      <AlreadySignedUpUsingGoogleDialog
        open={alreadySignedUpUsingGoogleDialogOpen}
        onClose={() => setAlreadySignedUpUsingGoogleDialogOpen(false)}
        email={loginEmail}
      />
    </>
  )
}

function AlreadySignedUpUsingGoogleDialog({ open, onClose, email }) {
  return (
    <AlertDialog
      open={open}
      onClose={onClose}
      title="Email already signed up using Google"
      contentText={
        <span>
          The email you used <strong>{email}</strong> is signed up using Google
          sign up in our system.{" "}
          <TextLink to="/user/set-password" text="Click here" /> to set a
          password for the customer portal.
        </span>
      }
    />
  )
}

AlreadySignedUpUsingGoogleDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  email: PropTypes.string,
}

function AlertDialog({ open, onClose, title, contentText }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

AlertDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  contentText: PropTypes.element,
}
